import { gsap } from 'gsap';
import type { ThreeStage } from '../../../../three/ThreeStage';
import { BaseScene } from '../../../../core/BaseScene';
import { draw } from '../../../../components/svg';
import { words, revealWords, settleWords } from '../../../../components/text';
import { renderMintTree } from '../../../../components/mintTree';

const VIEW_CLOSE = '300 120 1000 562';
const VIEW_FULL = '0 0 1600 900';

/**
 * 0 Pull back: the whole MINT family together
 * 1 Unnamed branches grow in every direction
 * 2 ONE FOUNDATION.
 * 3 MANY DIRECTIONS.
 */
export class MintDirectionsScene extends BaseScene {
  public readonly stepCount = 4;

  protected template(): string {
    return `
      <div class="scene-layout mint-directions">
        <svg class="diagram mt-diagram md-diagram" viewBox="${VIEW_FULL}" role="img" aria-label="MINT, its specialisations and unnamed future directions">
          ${renderMintTree(this.context, { uid: this.meta.id, ghosts: 'many', ghostFrom: 1 })}
        </svg>
        <div class="mt-statements">
          <h2 class="statement" data-from="2">${words('ONE FOUNDATION.')}</h2>
          <h2 class="statement statement--accent" data-from="3">${words('MANY DIRECTIONS.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    // Zoom back out from the last specialisation to the whole family.
    const tl = this.timeline();
    tl.fromTo(this.$('.md-diagram'), { attr: { viewBox: VIEW_CLOSE } }, { attr: { viewBox: VIEW_FULL }, duration: 2.2, ease: 'power3.inOut' }, 0);
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    gsap.set(this.$('.md-diagram'), { attr: { viewBox: VIEW_FULL } });
    draw(this.$$('.mt-branch__path'), true, { immediate: true });

    const ghostPaths = this.$$('.mt-ghost__path');
    const ghostDots = this.$$('.mt-ghost__glow, .mt-ghost__ring');
    if (forward && step === 1) {
      draw(ghostPaths, false, { immediate: true });
      tl.add(draw(ghostPaths, true, { duration: 1.2, stagger: 0.18, ease: 'power2.inOut' }), 0.1);
      tl.fromTo(ghostDots, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 1.1, stagger: 0.09, ease: 'power2.out' }, 0.9);
    } else {
      draw(ghostPaths, step >= 1, { immediate: true });
      gsap.set(ghostDots, { opacity: 1, scale: 1, transformOrigin: '50% 50%' });
    }

    // The statements take the centre, so the family recedes behind them.
    gsap.to(this.$('.md-diagram'), { opacity: step >= 2 ? 0.22 : 1, duration: immediate ? 0 : 0.9, ease: 'power2.inOut' });

    this.$$('.mt-statements .statement').forEach((s) => settleWords(s));
    if (forward && step >= 2) tl.add(revealWords(this.$$('.mt-statements .statement')[step - 2]), 0.2);
  }
}
